import { FusionInputField, RoadDefinerMap } from 'components/index';
import BaseModal from 'components/Modals/BaseModal';
import ConfirmModal from 'components/UI/ConfirmPopup';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { Marker } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  useCreateGroupMutation,
  useEditGroupMutation,
  useGetGroupsQuery,
} from 'services/devices/groups/groups.api';
import {
  selectUsgsProvisonalAccepted,
  setUsgsProvisonalAccepted,
} from 'store/slices/Widget/widgetSlice';
import { getDynamicDeviceIcon, getDynamicDeviceIconFromType } from 'utils/devices/icon.utils';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import { USGS_PROVISIONAL_STATEMENT } from '../../../ConnectedDevices/assets/USGSProvisionalStatment';
import {
  buildGroupPayload,
  calculateCenterAndZoom,
  getDeviceLocationsByIds,
} from '../../utils/groupUtils';
import DeviceMultiSelector from './components/DeviceMultiSelector';

// Shared add/edit group modal wrapper for the Connected Device Groups page.
export default function AddEditGroupModal({ modalOpen, setModalOpen }) {
  const [createGroup, { isLoading: creatingGroup }] = useCreateGroupMutation();
  const [editGroup, { isLoading: editingGroup }] = useEditGroupMutation();
  const { data: connectedDevices = [], isLoading: connectedDevicesLoading } =
    useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const provisionalAccepted = useSelector(selectUsgsProvisonalAccepted);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [mapCenter, setMapCenter] = useState();
  const [mapZoom, setMapZoom] = useState();
  const isFormLoading = connectedDevicesLoading;
  const dispatch = useDispatch();

  const getInitialFormData = (source = {}) => ({
    name: '',
    comments: '',
    devices: [],
    type: '',
    coords: [],
    ...source,
  });

  const initialData = useMemo(() => {
    if (modalOpen < 0) return {};
    return deviceGroups?.find((group) => group.id === modalOpen) || {};
  }, [deviceGroups, modalOpen]);

  const [formData, setFormData] = useState(getInitialFormData(initialData));
  const isEditMode = Object.keys(initialData).length > 0;

  // Keep form state in sync with selector and recalc map framing
  const onDevicesChanged = (value, deviceType) => {
    setFormData({ ...formData, devices: value, type: deviceType });

    const valueDevices = getDeviceLocationsByIds(value, connectedDevices);

    const { center, zoom } = calculateCenterAndZoom(valueDevices);
    setMapCenter(center);
    setMapZoom(zoom);
  };

  const resolveAddEditGroup = async (formData) => {
    const finalData = buildGroupPayload(formData, connectedDevices);

    try {
      if (modalOpen === -1) {
        await createGroup({ ...finalData }).unwrap();
        notifySuccessToast('Device group', { isAddOperation: true });
      } else if (modalOpen >= 0) {
        await editGroup({ ...finalData }).unwrap();
        notifySuccessToast('Device group', { isAddOperation: false });
      } else {
        return;
      }
    } catch (error) {
      console.error('Failed to add/edit group.', error);
      // The server says why when it refuses (a group that would mix simulated and real
      // devices, for one); the editor stays open so the selection can be fixed.
      notifyErrorToast(
        'Device group',
        { isAddOperation: modalOpen === -1 },
        { errorPayload: error },
      );
      return;
    }

    setModalOpen(-2);
  };

  useEffect(() => {
    const data =
      modalOpen >= 0 ? deviceGroups?.find((group) => group.id === modalOpen) || {} : {};
    setFormData(getInitialFormData(data));
  }, [modalOpen]);

  if (modalOpen <= -2) return null;

  const getDeviceMarker = (deviceId) => {
    const device = connectedDevices?.find((device) => device.id === deviceId);

    if (!device) return;

    // NO NAME ON THE MARKER — not as a child, not as a title. A child string is
    // rendered into the map container by react-leaflet, where it sits behind the
    // tile panes and flashes into view every time the zoom animation shifts them;
    // a title is a browser tooltip that pops up whenever the pointer rests over a
    // pin, which is most of a wheel zoom. The names are in the device list beside
    // the map.

    // Special case for USGS Monitoring Stations to use their specific icons
    // Needed because the icons rely on data inside the device, not just the device_type
    if (device.system_type === 'USGS' && device.device_type === 'Monitoring Station') {
      return (
        <Marker
          key={deviceId}
          draggable={false}
          position={device.location || { lat: 0, lng: 0 }}
          icon={getDynamicDeviceIcon(device, 'icon', [30, 30])}
        />
      );
    }

    return (
      <Marker
        key={deviceId}
        draggable={false}
        position={device.location || { lat: 0, lng: 0 }}
        icon={getDynamicDeviceIconFromType(device?.system_type, device?.device_type)}
      />
    );
  };

  return (
    <BaseModal
      show={modalOpen}
      handleShow={() => setModalOpen(-2)}
      title={isEditMode ? 'Edit Group' : 'New Group'}
      size="xl"
    >
      <div className="flex-lg-row flex-column w-100 d-flex justify-content-around">
        {/* Input Fields */}
        <div className="d-flex flex-row gap-3">
          <div className="d-flex flex-column overflow-hidden">
            <div>Group Name</div>
            <FusionInputField
              value={formData?.name}
              onChange={(value) => setFormData({ ...formData, name: value })}
            />

            <div className="d-flex flex-column overflow-hidden flex-shrink">
              <div>Comments</div>
              <div className="d-flex bg-transparent rounded-3 border border-1 border-white overflowy-hidden">
                <textarea
                  className="d-flex overflowy-hidden"
                  rows={1}
                  value={formData.comments}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;

                    setFormData({ ...formData, comments: e.target.value });
                  }}
                />
              </div>
            </div>

            {/* Device Selector */}
            <div className="mt-3 customscrollbar-y p-2 d-flex flex-column overflow-hidden flex-shrink bg-primary rounded-3 flex-grow-full min-h-sm">
              <div className="p-0">Device List</div>
              <DeviceMultiSelector
                value={formData.devices}
                deviceType={formData.type}
                onChange={onDevicesChanged}
              />
            </div>
          </div>
        </div>
        {/* Map input */}
        <div className="d-flex flex-column gap-3 mt-3 flex-grow-full max-w-lg-50">
          <RoadDefinerMap
            value={formData.coords || [mapCenter]}
            onChange={(value) => setFormData({ ...formData, coords: value })}
            initialCenter={mapCenter}
            initialZoom={mapZoom}
          >
            {formData.devices.map(getDeviceMarker)}
          </RoadDefinerMap>

          {/* USGS provisional attribution — always visible so users can review before adding USGS devices */}
          <div
            className="d-flex align-items-center justify-content-start gap-2 px-1"
            style={{ fontSize: 10 }}
          >
            <IoMdInformationCircleOutline
              className="rounded-circle bg-primary p-1 cursor-pointer flex-shrink-0"
              style={{ height: 26, width: 26 }}
              onClick={() => setShowDisclaimerModal(true)}
            />
            <p className="mb-0 text-white opacity-75">
              USGS Data may be{' '}
              <span
                className="text-decoration-underline cursor-pointer"
                onClick={() => setShowDisclaimerModal(true)}
              >
                provisional
              </span>
            </p>
          </div>

          {/* Submit button */}
          <div className="d-flex flex-row justify-content-end">
            <Button
              onClick={() => resolveAddEditGroup(formData)}
              disabled={isFormLoading || creatingGroup || editingGroup}
            >
              {creatingGroup || editingGroup ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </span>
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : isFormLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                  Loading devices...
                </>
              ) : isEditMode ? (
                'Save'
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={!provisionalAccepted || showDisclaimerModal}
        setOpen={setShowDisclaimerModal}
        options={['Continue']}
        callbacks={[
          () => {
            dispatch(setUsgsProvisonalAccepted(true));
            setShowDisclaimerModal(false);
          },
        ]}
        message={USGS_PROVISIONAL_STATEMENT}
        closeAction={() => {}}
        size="lg"
      />
    </BaseModal>
  );
}
